import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { setQuote } from "../../../services/adminService";
import {
  showSpinner,
  closeSpinner,
  modal,
  alert,
} from "../../../js/modules/Dialogs";
import ConfirmDialog from "../../Modal/ConfirmDialog";
import { sessionExpired, serverTimeOutMsg } from "../../../config/messages";
import { serverUrl, userImagesFolder, imageFormat, defaultAvatarImage } from "../../../config/env";
import { spoofedISODateProcess } from "../../../js/modules/Generic";
import { toScreen } from "../../../js/modules/Routing";
import { retrieveRatingsMav } from "../../../services/ratingService";
import EditRating from "./EditRating";
import withNavigate from "../withNavigate";
import TopNav from "../../commonUI/TopHeader/TopHeader";

const SORT_OPTIONS = {
  date:   { asc: '{"job.creationDate":1}',                        desc: '{"job.creationDate":-1}' },
  stars:  { asc: '{"ratings[0].stars":1}',                        desc: '{"ratings[0].stars":-1}' },
  client: { asc: '{"job._creator.accountInfo.firstname":1}',      desc: '{"job._creator.accountInfo.firstname":-1}' },
};

const STAR_VALUES = [5, 4, 3, 2, 1];

const StarRating = ({ stars, name, className = "c-rating__label-read-only-small" }) => (
  <fieldset className="c-rating__wrap">
    {STAR_VALUES.map((val) => (
      <React.Fragment key={val}>
        <input tabIndex="-1" className="c-rating__input" type="radio" name={name} value={val} checked={stars === val} readOnly />
        <label className={className}><span className="u-visually-hidden">{val} stars</span></label>
      </React.Fragment>
    ))}
  </fieldset>
);

const INITIAL_MAV = {
  _id: "", short_id: "",
  accountInfo: { firstname: "", surname: "", avatar: "" },
  totalRatings: 0, ratingAsMav: 0,
};

const Reviews = ({ userDetails, mavId, navigate }) => {
  const extraAttributes = window.history.state.usr;
  const editRatingRef = useRef(null);
  const mavShortId = mavId ?? window.location.pathname?.split("/")[2];

  const [sort, setSort] = useState(SORT_OPTIONS.date.asc);
  const [mavDetails, setMavDetails] = useState(INITIAL_MAV);
  const [ratings, setRatings] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ratingToDelete, setRatingToDelete] = useState(null);

  const handleApiError = useCallback((error) => {
    const status = error.response?.status;
    if (status === 401) {
      modal(sessionExpired);
      toScreen(false, navigate, "/logout");
    } else if (status === 504 || status === 502) {
      modal(serverTimeOutMsg);
      toScreen(false, navigate, "/welcome");
    } else if (status === 400) {
      alert(error.response?.data?.message || "Bad request.");
    } else {
      modal(error.message || "Something went wrong!");
    }
  }, [navigate]);

  const getMavRatings = useCallback(async () => {
    if (!userDetails.jwttoken || !userDetails.accountInfo.username) return;

    showSpinner();
    try {
      const resJson = await retrieveRatingsMav({
        accountInfo: {
          username: userDetails.accountInfo.username,
          email: userDetails.accountInfo.email,
        },
        short_id: mavShortId,
        contentmode: "full",
        profilemode: "full",
        sort,
        page: currentPage,
      });
      closeSpinner();
      setMavDetails(resJson._creator);
      setRatings(resJson._creator.ratings);
      setTotalPages(resJson.pagination.total);
    } catch (error) {
      closeSpinner();
      handleApiError(error);
    }
  }, [sort, currentPage, mavShortId, userDetails, handleApiError]);

  useEffect(() => {
    getMavRatings();
  }, [getMavRatings]);

  const sortRatings = (event, key) => {
    if (event) event.preventDefault();
    const { asc, desc } = SORT_OPTIONS[key] || {};
    if (asc) setSort((prev) => (prev === asc ? desc : asc));
  };

  const sortIcon = (key) => {
    const { asc, desc } = SORT_OPTIONS[key] || {};
    if (sort === desc) return " ↑";
    if (sort === asc) return " ↓";
    return "";
  };

  const editRating = (event, theKey) => {
    if (event) event.preventDefault();
    const r = ratings[theKey];
    editRatingRef.current.goEditRating(event, theKey, r.comment, r.stars, r.job._creator.accountInfo.firstname);
  };

  const saveRating = async (event, theKey, theComment, theStars) => {
    if (event) event.preventDefault();
    showSpinner();
    try {
      await setQuote({
        short_id: ratings[theKey].job.quote.short_id,
        ratings: [{ stars: parseInt(theStars), comment: theComment }],
      });
      closeSpinner();
      getMavRatings();
    } catch (error) {
      closeSpinner();
      handleApiError(error);
    }
  };

  const deleteRating = (event, theKey) => {
    if (event) event.preventDefault();
    setRatingToDelete(theKey);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteRating = () => {
    setShowDeleteConfirm(false);
    if (ratingToDelete !== null) {
      saveRating(null, ratingToDelete, false, false);
      setRatingToDelete(null);
    }
  };

  const nextPage = (e) => {
    if (e) e.preventDefault();
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const prevPage = (e) => {
    if (e) e.preventDefault();
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const avatarSrc = mavDetails.accountInfo?.avatar
    ? `${serverUrl}${userImagesFolder}/${mavDetails.short_id}/${mavDetails.accountInfo.avatar}${imageFormat}`
    : defaultAvatarImage;

  const ratingKeys = Object.keys(ratings);

  return (
    <>
      <TopNav title="Reviews" />

      <div className="global-body">
        <div className="global-body-container">

          {/* MAV profile card */}
          <div className="mav-profile-card">
            <div className="mav-profile-card__avatar">
              <img
                src={avatarSrc}
                alt="avatar"
                className="mav-profile-card__img"
                onError={(e) => { e.target.src = defaultAvatarImage; }}
                onClick={(e) => mavDetails.totalRatings > 0 && toScreen(e, navigate, `/mav/${mavDetails.short_id}`)}
              />
            </div>
            <div className="mav-profile-card__info">
              <p className="mav-profile-card__id">ID: <strong>{mavDetails.short_id}</strong></p>
              <p className="mav-profile-card__name">
                {mavDetails.accountInfo.firstname} {mavDetails.accountInfo.surname}
              </p>
              <div className="mav-profile-card__rating">
                <fieldset className="c-rating__wrap" onClick={(e) => toScreen(e, navigate, `/mav/${mavDetails.short_id}`)}>
                  {STAR_VALUES.map((val) => (
                    <React.Fragment key={val}>
                      <input tabIndex="-1" className="c-rating__input" type="radio" id={`star${val}`} name="stars" value={val} checked={mavDetails.ratingAsMav === val} readOnly />
                      <label className="c-rating__label-read-only" htmlFor={`star${val}`}>
                        <span className="u-visually-hidden">{val} stars</span>
                      </label>
                    </React.Fragment>
                  ))}
                </fieldset>
                <span className="mav-profile-card__review-count">
                  {mavDetails.totalRatings > 0 ? `${mavDetails.totalRatings} reviews` : "(No reviews yet)"}
                </span>
              </div>
            </div>
            <div className="mav-profile-card__actions">
              <button type="button" className="c-btn c-btn--3 c-btn--small c-btn--black" onClick={(e) => toScreen(e, navigate, `/mav/${mavDetails.short_id}`)}>
                View MAV
              </button>
              <button
                type="button"
                className="c-btn c-btn--3 c-btn--small"
                onClick={(e) => toScreen(e, navigate, `/message/${mavDetails.short_id}`, false, {
                  ...extraAttributes,
                  previousPath: window.location.pathname ?? window.history.state.usr?.previousPath,
                })}
              >
                Contact MAV
              </button>
            </div>
          </div>

          {/* Reviews table */}
          <div className="table-responsive">
            <table className="table clientTable">
              <thead>
                <tr>
                  <th>Review</th>
                  <th><button className="c-btn--sort" onClick={(e) => sortRatings(e, "stars")}>Stars{sortIcon("stars")}</button></th>
                  <th><button className="c-btn--sort" onClick={(e) => sortRatings(e, "client")}>Client{sortIcon("client")}</button></th>
                  <th><button className="c-btn--sort" onClick={(e) => sortRatings(e, "date")}>Job Date{sortIcon("date")}</button></th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ratingKeys.length > 0 ? ratingKeys.map((key) => {
                  const review = ratings[key];
                  const creator = review.job._creator.accountInfo;
                  return (
                    <tr key={key}>
                      <td>
                        {review.comment
                          ? `"${review.comment}"`
                          : <span className="text-muted">(no comment)</span>}
                      </td>
                      <td><StarRating stars={review.stars} name={`stars${key}`} /></td>
                      <td>{creator.firstname} {creator.surname}</td>
                      <td>
                        {review.job.creationDate
                          ? spoofedISODateProcess(review.job.creationDate, "printDateNice")
                          : "(in progress)"}
                      </td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end" }}>
                          <button type="button" className="c-btn c-btn--3 c-btn--small c-btn--black" onClick={(e) => toScreen(e, navigate, `/job/${review.job.short_id}`)}>
                            View Job
                          </button>
                          <button type="button" className="c-btn c-btn--3 c-btn--small c-btn--green" onClick={(e) => editRating(e, key)}>
                            Edit
                          </button>
                          <button type="button" className="c-btn c-btn--3 c-btn--small c-btn--red" onClick={(e) => deleteRating(e, key)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={5} className="u-text-align-center u-margin-top-bottom-m u-color-red">
                      No reviews found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="c-pagination">
            <button type="button" className="c-pagination__prev" onClick={prevPage}>&lt;&lt; Prev</button>
            {currentPage} / {totalPages}
            <button type="button" className="c-pagination__next" onClick={nextPage}>Next &gt;&gt;</button>
          </div>

          <EditRating ref={editRatingRef} saveRating={saveRating} />
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete Review"
        message="Are you sure you want to delete this review?"
        onConfirm={handleConfirmDeleteRating}
        onCancel={() => { setShowDeleteConfirm(false); setRatingToDelete(null); }}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

Reviews.propTypes = {
  userDetails: PropTypes.object.isRequired,
  mavId: PropTypes.string.isRequired,
  updateState: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigate(Reviews);
